import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { isNull, get } from 'lodash/fp';
import { InputGroup, Input } from 'rsuite';
import { useDebounce } from 'use-debounce';
import {
  getIsResevationCompleted,
  getViewReservationGuestInfo
} from '../../../redux/Reservation/selectors/routes/view';
import {
  fetchInventoryItemAvailabilityByLocation as fetchInventoryItemAvailability,
  fetchInventoryPackageAvailabilityByLocation as fetchInventoryPackageAvailability
} from '../../../redux/Reservation/sagas/routines';
import {
  getIsFetchingItemAvailability,
  getItemAvailabilityForUniqKey,
  getItemSumQuantityForUniqKey,
  getItemInitialSumQuantityForUniqKey
} from '../../../redux/Reservation/selectors/routes/orders';
import { parseInt } from '../../../helpers/utils';
import guestQuantityLimit from '../../../helpers/guestQuantityLimit';
import getItemAvailabilityUniqId from '../../../helpers/getItemAvailabilityUniqId';
import { PicklistItemType } from '../../../interfaces/reservation';
import {
  StyledListItem,
  StyledInputGroup,
  HealthIndicator,
  LinkedItemIcon,
  ItemPhoto,
  ItemLimit
} from './elements';

const getItemLimit = (guestInfo, limitRules, categoryLimit) => {
  const itemLimit = limitRules ? guestQuantityLimit(guestInfo, limitRules) : 0;
  if (itemLimit === 0 || (categoryLimit > 0 && itemLimit > categoryLimit))
    return categoryLimit;

  return itemLimit;
};

const PicklistItem = ({
  data,
  orderItem,
  limitReached,
  categoryLimit,
  onChangeOrderItem
}) => {
  const dispatch = useDispatch();
  const { quantity } = orderItem;
  const { name, photo, healthLevel, limitRules, inventorySnapshot } = data;
  const isCompleted = useSelector(getIsResevationCompleted);
  const guestInfo = useSelector(getViewReservationGuestInfo);
  const isFetchingAvailability = useSelector(getIsFetchingItemAvailability);
  const itemUniqKey = getItemAvailabilityUniqId(inventorySnapshot);
  const itemAvailability = useSelector(state =>
    getItemAvailabilityForUniqKey(state, itemUniqKey)
  );
  const itemSumQuantity = useSelector(state =>
    getItemSumQuantityForUniqKey(state, itemUniqKey)
  );
  const initialSumQuantity = useSelector(state =>
    getItemInitialSumQuantityForUniqKey(state, itemUniqKey)
  );
  const availableQuantity =
    get('quantity', itemAvailability) - get('onholdQuantity', itemAvailability);

  const isLinkedItem = !!inventorySnapshot;
  const exceededInventoryLimit = useMemo(
    () => {
      if (!isLinkedItem || !itemSumQuantity) return false;
      return itemSumQuantity - initialSumQuantity > availableQuantity;
    },
    [itemSumQuantity, availableQuantity]
  );

  const hasLimit = !isNull(limitRules);
  const itemLimit = getItemLimit(guestInfo, limitRules, categoryLimit);
  const disabledIncrement =
    exceededInventoryLimit ||
    limitReached ||
    (hasLimit && quantity >= itemLimit);
  const restrictChanges = isCompleted && isLinkedItem;

  const handleInputChange = value => {
    const newQuantity = value ? parseInt(value) : 0;
    if ((newQuantity && newQuantity < 0) || restrictChanges) return;

    const hasReachedLimit = limitReached && newQuantity > quantity;
    const hasReachedItemLimit = hasLimit && newQuantity > itemLimit;
    const hasReachedInventoryLimit =
      newQuantity > quantity && exceededInventoryLimit;

    if (!hasReachedLimit && !hasReachedItemLimit && !hasReachedInventoryLimit) {
      onChangeOrderItem({ ...orderItem, quantity: newQuantity });
    }
  };

  const handleIncrement = () => {
    if (restrictChanges) return;
    const newQuantity = quantity + 1;
    const hasReachedLimit = limitReached && newQuantity > quantity;
    const hasReachedItemLimit = hasLimit && newQuantity > itemLimit;

    if (!hasReachedLimit && !hasReachedItemLimit && !exceededInventoryLimit) {
      onChangeOrderItem({ ...orderItem, quantity: newQuantity });
    }
  };

  const handleDecrement = () => {
    if (quantity === 0 || restrictChanges) return;
    onChangeOrderItem({ ...orderItem, quantity: quantity - 1 });
  };

  const [debouncedQuantity] = useDebounce(quantity, 500);
  useEffect(
    () => {
      if (isFetchingAvailability) return;
      if (isLinkedItem && debouncedQuantity > 0 && !itemAvailability) {
        const id = get('item.id', inventorySnapshot);
        const locationId = get('location.id', inventorySnapshot);
        const zoneId = get('zone.id', inventorySnapshot);
        dispatch(
          zoneId
            ? fetchInventoryItemAvailability({ id, locationId, zoneId })
            : fetchInventoryPackageAvailability({ id, locationId })
        );
      }
    },
    [debouncedQuantity]
  );

  const selectedClass = quantity > 0 ? 'selected' : '';
  const errorClass = quantity > 0 && exceededInventoryLimit ? 'error' : '';
  const disabledClass = restrictChanges ? 'disabled' : '';

  return (
    <StyledListItem
      className={`${selectedClass} ${errorClass} ${disabledClass}`}
    >
      {parseInt(healthLevel) > 0 && <HealthIndicator value={healthLevel} />}
      {isLinkedItem && <LinkedItemIcon inventorySnapshot={inventorySnapshot} />}
      {photo && <ItemPhoto alt="" image={photo} width={64} />}
      {hasLimit && <ItemLimit>Max: {itemLimit}</ItemLimit>}
      <p>{name}</p>
      <StyledInputGroup>
        <InputGroup.Addon
          onClick={handleDecrement}
          disabled={restrictChanges}
          className={quantity === 0 || restrictChanges ? 'disabled' : ''}
        >
          &nbsp;&nbsp;&ndash;
        </InputGroup.Addon>
        <Input
          value={quantity}
          style={{ textAlign: 'center' }}
          readOnly={restrictChanges}
          onChange={handleInputChange}
        />
        <InputGroup.Addon
          onClick={handleIncrement}
          disabled={disabledIncrement || restrictChanges}
          className={disabledIncrement || restrictChanges ? 'disabled' : ''}
        >
          &nbsp; +
        </InputGroup.Addon>
      </StyledInputGroup>
    </StyledListItem>
  );
};

PicklistItem.propTypes = {
  data: PicklistItemType.isRequired,
  orderItem: PropTypes.shape({
    quantity: PropTypes.number.isRequired,
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    picklistCategoryId: PropTypes.string.isRequired
  }).isRequired,
  onChangeOrderItem: PropTypes.func.isRequired,
  categoryLimit: PropTypes.number.isRequired,
  limitReached: PropTypes.bool.isRequired
};

export default PicklistItem;
